// @ts-nocheck
import * as React from "react";
import { createPortal } from "react-dom";

export type RankedCandidate = {
  place_id: string;
  name: string;
  formatted_address?: string;
  score?: number; // 0..1
  breakdown?: { name?: number; website?: number; geo?: number; pop?: number };
  explanation?: string;
};

type ConfirmPlaceSheetProps = {
  open: boolean;
  candidates: RankedCandidate[];
  onConfirm: (place: RankedCandidate) => void;
  onCancel: () => void;
  confirmLabel?: string;          // i18n
  cancelLabel?: string;           // i18n
  titleLabel?: string;            // i18n
  descLabel?: string;             // i18n
  closeOnBackdrop?: boolean;
  maxOptions?: number;            // default 3
  portalTargetId?: string;        // default document.body
};

function clamp01(x: unknown): number {
  const n = typeof x === "number" ? x : Number(x);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
}

function useScrollLock(locked: boolean) {
  React.useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [locked]);
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const sel = [
    "a[href]", "button:not([disabled])", "textarea:not([disabled])",
    "input:not([disabled])", "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(",");
  return Array.from(container.querySelectorAll<HTMLElement>(sel))
    .filter(el =>
      !el.hasAttribute("disabled") &&
      el.tabIndex !== -1 &&
      el.getAttribute("aria-hidden") !== "true"
    );
}

function scoreToPct(score?: number) {
  return Math.round(clamp01(score) * 100);
}
function scoreColor(pct: number) {
  // 0–49 red, 50–79 amber, 80+ green
  if (pct >= 80) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-rose-500";
}

export const ConfirmPlaceSheet: React.FC<ConfirmPlaceSheetProps> = ({
  open,
  candidates,
  onConfirm,
  onCancel,
  confirmLabel = "Choose",
  cancelLabel = "Cancel",
  titleLabel = "Confirm the place",
  descLabel = "We found a few likely matches. Pick the correct venue:",
  closeOnBackdrop = true,
  maxOptions = 3,
  portalTargetId,
}) => {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const lastActiveRef = React.useRef<HTMLElement | null>(null);
  const [selectedIdx, setSelectedIdx] = React.useState(0);

  const items = Array.isArray(candidates) ? candidates.slice(0, maxOptions) : [];
  const ids = React.useMemo(
    () => ({
      title: "confirm-place-title",
      desc: "confirm-place-desc",
      live: "confirm-place-live",
      group: "confirm-place-group",
    }),
    []
  );

  // Lock scroll when open
  useScrollLock(open);

  // Save/restore focus
  React.useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement as HTMLElement | null;
      // Focus first focusable
      const p = panelRef.current;
      if (p) {
        const els = getFocusable(p);
        (els[0] || p).focus();
      }
    } else {
      lastActiveRef.current?.focus?.();
    }
  }, [open]);

  // Keep selected index in range when the list changes
  React.useEffect(() => {
    if (selectedIdx > items.length - 1) setSelectedIdx(0);
  }, [items.length, selectedIdx]);

  if (!open) return null;

  const handleBackdropMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onCancel();
  };

  const onPanelKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    // Full focus trap
    if (e.key === "Tab") {
      const p = panelRef.current; if (!p) return;
      const els = getFocusable(p); if (els.length === 0) return;
      const first = els[0], last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !p.contains(active)) { e.preventDefault(); last.focus(); }
      } else {
        if (active === last || !p.contains(active)) { e.preventDefault(); first.focus(); }
      }
      return;
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      onCancel();
      return;
    }
  };

  const onGroupKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const chosen = items[selectedIdx];
      if (chosen) onConfirm(chosen);
    }
  };

  const body = (
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/40 backdrop-blur-[1px]"
      onMouseDown={handleBackdropMouseDown}
      aria-hidden={false}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ids.title}
        aria-describedby={ids.desc}
        className="w-full max-w-xl rounded-t-2xl bg-white p-4 shadow-xl outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 sm:rounded-2xl sm:my-10"
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 id={ids.title} className="text-lg font-semibold">{titleLabel}</h3>
          <button
            type="button"
            className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
        </div>

        <p id={ids.desc} className="mb-3 text-sm text-gray-600">
          {descLabel}
        </p>

        {/* Live region to announce selection changes */}
        <p id={ids.live} className="sr-only" aria-live="polite" />

        {/* List as a radiogroup for proper keyboard navigation */}
        <div
          role="radiogroup"
          aria-labelledby={ids.title}
          aria-describedby={ids.desc}
          className="space-y-3 max-h-[60vh] overflow-y-auto pr-1"
          onKeyDown={onGroupKeyDown}
        >
          {items.map((c, idx) => {
            const pct = scoreToPct(c.score);
            const checked = idx === selectedIdx;
            const radioId = `place-radio-${c.place_id}`;

            return (
              <div
                key={c.place_id}
                role="radio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                id={radioId}
                className={`rounded border p-3 outline-none transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-400 ${checked ? "ring-1 ring-blue-300 bg-blue-50/40" : ""}`}
                onClick={() => { setSelectedIdx(idx); onConfirm(c); }}
                onFocus={() => setSelectedIdx(idx)}
                aria-describedby={`${radioId}-meta`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate" title={c.name}>{c.name}</p>
                    {c.formatted_address && (
                      <p className="text-sm text-gray-600 truncate" title={c.formatted_address}>
                        {c.formatted_address}
                      </p>
                    )}

                    <div className="mt-2">
                      {/* Prefer native meter element */}
                      <meter
                        min={0}
                        max={100}
                        value={pct}
                        aria-label="Match confidence"
                        className="h-2 w-full rounded bg-gray-100"
                      />
                      <div className="h-2 w-full rounded bg-gray-100 -mt-2 overflow-hidden pointer-events-none">
                        <div className={`h-2 ${scoreColor(pct)}`} style={{ width: `${pct}%` }} />
                      </div>

                      <p id={`${radioId}-meta`} className="mt-1 text-xs text-gray-500">
                        <span className="mr-2 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-700 tabular-nums align-middle">{pct}%</span>
                        {c.breakdown
                          ? [
                              c.breakdown.name != null ? `name ${c.breakdown.name.toFixed(2)}` : null,
                              c.breakdown.website != null ? `website ${c.breakdown.website.toFixed(2)}` : null,
                              c.breakdown.geo != null ? `geo ${c.breakdown.geo.toFixed(2)}` : null,
                              c.breakdown.pop != null ? `pop ${c.breakdown.pop.toFixed(2)}` : null
                            ].filter(Boolean).join(" • ")
                          : (c.explanation || "Ranked by name match, website, geo bias, and popularity.")
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                      onClick={(e) => { e.stopPropagation(); onConfirm(c); }}
                    >
                      {confirmLabel}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 text-xs text-gray-500">
          These are ranked using the name match, website domain, city hint, and popularity signals.
        </div>
      </div>
    </div>
  );

  // SSR-safe portal target
  if (typeof document === "undefined") return body;
  const target = portalTargetId ? document.getElementById(portalTargetId) : null;
  return createPortal(body, target || document.body);
};

export default ConfirmPlaceSheet;
